import { useMutation } from '@tanstack/react-query';
import {
  authenticateMagicLink,
  MagicLinkAuthErrorCode,
  MagicLinkAuthSuccessResponse,
  MagicLinkRequestBody,
  MagicLinkRequestResponse,
  persistPortalSession,
  requestMagicLink,
  resendMagicLinkEmail,
} from '@api/customer-portal-api';
import { capturePortalEvent, getMonitoringErrorCategory } from '@lib/monitoring';

export class MagicLinkAuthenticationError extends Error {
  code: MagicLinkAuthErrorCode;

  constructor(code: MagicLinkAuthErrorCode) {
    super(code === 'link_expired' ? 'Magic link expired' : 'Magic link invalid');
    this.name = 'MagicLinkAuthenticationError';
    this.code = code;
  }
}

export const useRequestMagicLink = () =>
  useMutation<MagicLinkRequestResponse, unknown, MagicLinkRequestBody>({
    mutationFn: async (body) => {
      try {
        const res = await requestMagicLink(body);
        if (!res?.success) {
          throw Object.assign(new Error(res?.message || 'Failed to send magic link'), {
            monitoringCategory: 'request_unsuccessful',
          });
        }

        capturePortalEvent('portal_magic_link_requested', {
          request_type: 'initial',
          store_domain: body.domain,
        });
        return res;
      } catch (error) {
        capturePortalEvent('portal_magic_link_request_failed', {
          request_type: 'initial',
          store_domain: body.domain,
          error_category: getMonitoringErrorCategory(error),
        });
        throw error;
      }
    },
  });

export const useResendMagicLink = () =>
  useMutation<MagicLinkRequestResponse, unknown, MagicLinkRequestBody>({
    mutationFn: async (body) => {
      try {
        const res = await resendMagicLinkEmail(body);
        if (!res?.success) {
          throw Object.assign(new Error(res?.message || 'Failed to resend magic link'), {
            monitoringCategory: 'request_unsuccessful',
          });
        }

        capturePortalEvent('portal_magic_link_requested', {
          request_type: 'resend',
          store_domain: body.domain,
        });
        return res;
      } catch (error) {
        capturePortalEvent('portal_magic_link_request_failed', {
          request_type: 'resend',
          store_domain: body.domain,
          error_category: getMonitoringErrorCategory(error),
        });
        throw error;
      }
    },
  });

export const useAuthenticateMagicLink = () =>
  useMutation<MagicLinkAuthSuccessResponse, MagicLinkAuthenticationError, string>({
    mutationFn: async (token) => {
      try {
        const res = await authenticateMagicLink(token);
        if (!res.success) {
          throw new MagicLinkAuthenticationError(res.error);
        }

        persistPortalSession(res);
        capturePortalEvent('portal_session_started', {
          customer_id: res.customer_id,
          store_id: res.store_id,
        });
        return res;
      } catch (error) {
        capturePortalEvent('portal_session_start_failed', {
          error_category: getMonitoringErrorCategory(error),
        });
        throw error;
      }
    },
  });
